import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Checkbox,
  Grid,
  styled,
} from '@mui/material';
import { AppTextInput } from 'components/FormElements';
import { AllocationRule } from 'further-types/lp/allocations';
import { DrawdownSourcePriority } from 'further-types/lp/capital-action';
import { CurrencyCode } from 'further-types/lp/vehicle';
import BasicSelect from 'further-ui/components/BasicSelect';
import FormField from 'further-ui/components/FormField';
import { Column } from 'further-ui/components/Layout';
import Table from 'further-ui/components/Table';
import { getCurrencySymbol, numberToCurrencyString } from 'further-ui/utils';
import { round } from 'lodash';
import React, { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { NumericFormat } from 'react-number-format';
import { UpdateCapitalActionFormValues } from '../../../hooks/useCapitalActionForm';
import useAllocateCapitalCall from './hooks/useAllocateCapitalCall';
import useAllocationChangeHandler from './hooks/useAllocationChangeHandler';
import useRecallableDistributionCalledInOtherEntries from './hooks/useRecallableDistributionCalledInOtherEntries';

const StyledAccordion = styled(Accordion)({
  width: '100%',
});

const AccordionHeader = styled(AccordionSummary)(({ theme }) => ({
  ...theme.typography.h6,
  border: `1px solid ${theme.palette.grey[300]}`,
}));

const AccordionBody = styled(AccordionDetails)({
  padding: 0,
});

const getDrawdownSourcePriorities = (showUncalled: boolean) => [
  {
    value: DrawdownSourcePriority.UnallocatedCommitmentFirst,
    label: showUncalled
      ? `From uncalled commitment first`
      : `From unallocated commitment first`,
  },
  {
    value: DrawdownSourcePriority.RecallableDistributionFirst,
    label: `From recallable distribution first`,
  },
  {
    value: DrawdownSourcePriority.Manual,
    label: `Manual allocation`,
  },
];

type Props = {
  entryIndex: number;
  title: string;
  calledFundsLabel: string;
  currency: CurrencyCode;
  allocationRule: AllocationRule;
  capitalCalledLcy: number;
  vehicleId: string;
  actionId?: string;
  showUncalledCommitment?: boolean;
  onChange: (
    capitalBreakdown: Array<{
      commitmentId: string;
      amountLcy: number;
      fundsCalled?: {
        fromCommitmentLcy: number;
        fromRecallableDistributionLcy: number;
      };
    }>,
  ) => void;
  capitalBreakdown: Array<{
    commitmentId: string;
    amountLcy: number;
    fundsCalled?: {
      fromCommitmentLcy: number;
      fromRecallableDistributionLcy: number;
    };
  }>;
  keepCallAmountFixed: boolean;
};

const CapitalCallAllocations: React.FC<Props> = ({
  title,
  currency,
  allocationRule,
  capitalCalledLcy,
  vehicleId,
  actionId,
  entryIndex,
  onChange,
  capitalBreakdown,
  calledFundsLabel,
  keepCallAmountFixed,
  showUncalledCommitment = false,
}) => {
  const [excludedCommitmentIds, setExcludedCommitmentIds] = useState<
    Array<string>
  >(
    capitalBreakdown
      .filter((cb) => cb.amountLcy === 0)
      .map((c) => c.commitmentId),
  );

  const { control, watch, setValue } =
    useFormContext<UpdateCapitalActionFormValues>();

  const drawdownSourcePriority =
    watch(`entries.${entryIndex}.drawdownSourcePriority`) ??
    DrawdownSourcePriority.UnallocatedCommitmentFirst;

  const recallableDistributionCalledInOtherEntries =
    useRecallableDistributionCalledInOtherEntries(entryIndex);

  const {
    hasRecallableDistribution,
    isPending,
    calledCapitalAllocations,
    allocateCapitalCall,
  } = useAllocateCapitalCall({
    vehicleId,
    actionId,
    capitalCalledLcy,
    excludedCommitmentIds,
    drawdownSourcePriority,
    allocationRule,
    recallableDistributionCalledInOtherEntries,
    existingBreakdown: capitalBreakdown,
    onSuccess: onChange,
  });

  const handleAllocationChange = useAllocationChangeHandler({
    capitalBreakdown,
    onAllocationsChange: onChange,
    keepCallAmountFixed,
  });

  const handleManualChange = () => {
    if (!hasRecallableDistribution) {
      return;
    }
    setValue(
      `entries.${entryIndex}.drawdownSourcePriority`,
      DrawdownSourcePriority.Manual,
    );
  };

  const handleDrawdownSourcePriorityChange = (
    drawdownSourcePriority: DrawdownSourcePriority,
  ) => {
    setValue(
      `entries.${entryIndex}.drawdownSourcePriority`,
      drawdownSourcePriority,
    );

    if (drawdownSourcePriority !== DrawdownSourcePriority.Manual) {
      allocateCapitalCall({
        useExistingBreakdown: false,
        drawdownSourcePriority,
      });
    }
  };

  const currencySymbol = getCurrencySymbol(currency);

  const columns = [
    {
      label: 'Investor',
      key: 'investorLabel',
    },
    {
      label: 'Total commitment',
      key: 'totalCommitmentLcy',
    },
    {
      label: showUncalledCommitment
        ? 'Uncalled commitment'
        : 'Unallocated commitment',
      key: showUncalledCommitment
        ? 'uncalledCommitmentLcy'
        : 'unallocatedCommitmentLcy',
    },
    ...(hasRecallableDistribution
      ? [
          {
            label: 'Recallable distribution',
            key: 'recallableDistributionLcy',
          },
          {
            label: 'Total callable capital',
            key: 'availableToBeCalledLcy',
          },
        ]
      : []),
    {
      label: 'Close date',
      key: 'closeDate',
    },
    ...(hasRecallableDistribution
      ? [
          {
            label: calledFundsLabel,
            key: 'amountLcy',
            render: (row: { commitmentId: string }) =>
              numberToCurrencyString(
                capitalBreakdown.find(
                  (c) => c.commitmentId === row.commitmentId,
                )?.amountLcy ?? 0,
                { currency },
              ),
          },
        ]
      : []),

    {
      label: hasRecallableDistribution
        ? showUncalledCommitment
          ? 'From uncalled commitment'
          : 'From unallocated commitment'
        : calledFundsLabel,
      key: 'fromUnallocatedCommitmentLcy',
      render: (row: { commitmentId: string }) => {
        const capital = capitalBreakdown.find(
          (c) => c.commitmentId === row.commitmentId,
        );

        return (
          <NumericFormat
            value={capital?.fundsCalled?.fromCommitmentLcy ?? 0}
            onValueChange={({ floatValue }, e) => {
              handleAllocationChange(
                row.commitmentId,
                'fromCommitmentLcy',
                floatValue ?? 0,
              );
              if (e.source === 'event') {
                handleManualChange();
              }
            }}
            allowNegative={false}
            decimalScale={5}
            allowLeadingZeros={false}
            thousandSeparator={true}
            prefix={`${currencySymbol} `}
            customInput={AppTextInput}
            placeholder="Enter amount"
            disabled={
              isPending || (keepCallAmountFixed && !hasRecallableDistribution)
            }
          />
        );
      },
    },
    ...(hasRecallableDistribution
      ? [
          {
            label: 'From recallable distribution',
            key: 'fromRecallableDistributionLcy',
            render: (row: { commitmentId: string }) => {
              const capital = capitalBreakdown.find(
                (c) => c.commitmentId === row.commitmentId,
              );
              return (
                <NumericFormat
                  value={
                    capital?.fundsCalled?.fromRecallableDistributionLcy ?? 0
                  }
                  onValueChange={({ floatValue }, e) => {
                    handleAllocationChange(
                      row.commitmentId,
                      'fromRecallableDistributionLcy',
                      floatValue ?? 0,
                    );
                    if (e.source === 'event') {
                      handleManualChange();
                    }
                  }}
                  allowNegative={false}
                  decimalScale={5}
                  allowLeadingZeros={false}
                  thousandSeparator={true}
                  prefix={`${currencySymbol} `}
                  customInput={AppTextInput}
                  placeholder="Enter amount"
                  disabled={isPending}
                />
              );
            },
          },
        ]
      : []),

    {
      label: 'Exclude from allocation',
      key: 'excludeFromAllocation',
      render: (row: {
        commitmentId: string;
        excludeFromAllocation: boolean;
      }) => (
        <Checkbox
          disabled={isPending || !!actionId}
          checked={row.excludeFromAllocation}
          onChange={(e) => {
            setExcludedCommitmentIds(
              e.target.checked
                ? [...excludedCommitmentIds, row.commitmentId]
                : excludedCommitmentIds.filter((id) => id !== row.commitmentId),
            );
          }}
        />
      ),
    },
  ];

  const rows = calledCapitalAllocations.map((allocation) => ({
    id: allocation.commitmentId,
    commitmentId: allocation.commitmentId,
    investorLabel: allocation.investorLabel,
    totalCommitmentLcy: numberToCurrencyString(allocation.totalCommitmentLcy, {
      currency,
    }),
    uncalledCommitmentLcy: numberToCurrencyString(
      allocation.uncalledCommitmentLcy,
      { currency },
    ),
    unallocatedCommitmentLcy: numberToCurrencyString(
      allocation.unallocatedCommitmentLcy,
      { currency },
    ),
    closeDate: allocation.closeDate.toLabel(),

    allocationPercentage: `${round(allocation.allocationPercentage, 2)}%`,
    recallableDistributionLcy: numberToCurrencyString(
      allocation.recallableDistributionLcy,
      { currency },
    ),
    availableToBeCalledLcy: numberToCurrencyString(
      allocation.availableToBeCalledLcy,
      { currency },
    ),
    excludeFromAllocation: allocation.excludeFromAllocation,
  }));

  return (
    <Column spacing="md">
      {hasRecallableDistribution && (
        <Grid container>
          <Grid size={{ xs: 12, md: 6 }}>
            <FormField label="Call funds">
              <Controller
                name={`entries.${entryIndex}.drawdownSourcePriority`}
                control={control}
                render={({ fieldState: { error }, field }) => (
                  <BasicSelect
                    {...field}
                    fullWidth
                    placeholder="Select drawdown source priority"
                    options={getDrawdownSourcePriorities(
                      showUncalledCommitment,
                    )}
                    disabled={isPending}
                    error={!!error}
                    onChange={handleDrawdownSourcePriorityChange}
                  />
                )}
              />
            </FormField>
          </Grid>
        </Grid>
      )}

      <StyledAccordion defaultExpanded>
        <AccordionHeader expandIcon={<ExpandMoreIcon />}>
          {title}
        </AccordionHeader>
        <AccordionBody>
          <Table
            showHeaderBorder
            rows={rows}
            columns={columns}
            isLoading={isPending}
          />
        </AccordionBody>
      </StyledAccordion>
    </Column>
  );
};

export default CapitalCallAllocations;
